import { useAuthContext } from "@hooks/context/useAuthContext";
import { useNotificationContext } from "@hooks/context/useNotificationContext";
import { useProfileContext } from "@hooks/context/useProfileContext";
import {
  SUPPORT_ACTION_ID,
  findSupportAction,
  useSupportActions
} from "@hooks/utils/support/useSupportActions";
import { useInstallPrompt } from "@hooks/utils/useInstallPrompt";
import { ClickAwayListener, Skeleton } from "@mui/material";
import type { ActionDialAction } from "@ui/atoms/ActionDial/ActionDial";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import Icon from "@ui/atoms/Icon/Icon";
import Logo from "@ui/atoms/Logo/Logo";
import { ConfirmationDialog } from "@ui/molecules/ConfirmationDialog/ConfirmationDialog";
import { applyIconProps } from "@utils/icon";
import * as RouteHelper from "@utils/paths";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ProfileAnchor,
  ProfileAvatar,
  ProfileAvatarWrap,
  ProfileCard,
  ProfileFooter,
  ProfileFooterRule,
  ProfileMenu,
  ProfileMenuBadge,
  ProfileMenuIcon,
  ProfileMenuItem,
  ProfileMenuLabel,
  ProfileName,
  ProfileOrg,
  ProfileRailMenu,
  ProfileRailMenuBadge,
  ProfileRailMenuButton,
  ProfileStatusDot
} from "./MainSidebarProfileSection.components";
import { FoldingChevron, FoldingText } from "../MainSidebar.components";

export interface Props {
  isDrawOpen: boolean;
  loading: boolean;
  onMenuOpenChange?: (open: boolean) => void;
}

const CARD_AVATAR = 32;
const MENU_ICON_SIZE = 16;
const LOGO_FALLBACK_SIZE = 18;

/**
 * Contact Support is the one action the menu accents. Asked here rather than
 * at each menu, so the expanded stack and the collapsed rail cannot end up
 * highlighting different rows.
 */
const isHighlighted = (action: ActionDialAction): boolean =>
  action.dataTestId === SUPPORT_ACTION_ID.contactSupport;

/**
 * The user card at the foot of the nav, and the menu it opens. Click to open
 * — the previous launcher opened on hover, which the design drops: the menu
 * blurs the whole nav behind it, so a pointer sweeping past the card must not
 * be able to trigger that.
 *
 * Expanded, the menu is a stack of cards rising from the launcher; collapsed,
 * a column of round buttons rising from the avatar. Same actions either way.
 */
const MainSidebarProfileSection = ({
  isDrawOpen,
  loading,
  onMenuOpenChange
}: Props) => {
  const navigate = useNavigate();
  const { isSocialProAdmin, signOut } = useAuthContext();
  const { isAuthProfileLoading, userOrganisation, userProfile } =
    useProfileContext();
  const { openNotificationDrawer, unreadCount } = useNotificationContext();
  const { canInstall, promptInstall } = useInstallPrompt();
  const supportActions = useSupportActions();

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isLoading = loading || isAuthProfileLoading;

  const setMenuOpen = useCallback(
    (open: boolean) => {
      setIsMenuOpen(open);
      onMenuOpenChange?.(open);
    },
    [onMenuOpenChange]
  );

  const handleLogout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const actions = useMemo((): ActionDialAction[] => {
    // By id, not by index: the assistant is conditional, so its position in the
    // list is not something the hook can promise.
    const contactSupportAction = findSupportAction(
      supportActions,
      SUPPORT_ACTION_ID.contactSupport
    );
    const assistantAction = findSupportAction(
      supportActions,
      SUPPORT_ACTION_ID.assistant
    );

    const list: ActionDialAction[] = [];

    if (contactSupportAction) list.push(contactSupportAction);

    list.push(
      {
        dataTestId: "account-settings-button",
        icon: <Icon name="settings" />,
        onClick: () => navigate(RouteHelper.settings),
        title: "Settings"
      },
      {
        dataTestId: "notifications-button",
        icon: <Icon name="notification" />,
        onClick: openNotificationDrawer,
        showBadge: unreadCount > 0,
        title: "Notifications"
      }
    );

    if (assistantAction) list.push(assistantAction);

    // Only shown on browsers that support installing the PWA and where it isn't
    // already installed (Chrome/Edge, desktop). Elsewhere `canInstall` is false.
    if (canInstall) {
      list.push({
        dataTestId: "install-app-button",
        icon: <Icon name="laptop" />,
        onClick: promptInstall,
        title: "Install app"
      });
    }

    if (isSocialProAdmin) {
      list.push({
        dataTestId: "social-pro-admin-button",
        icon: <Icon name="sliders" />,
        onClick: () => navigate(RouteHelper.socialProAdmin),
        title: "Catchment Admin"
      });
    }

    list.push({
      dataTestId: "logout-button",
      icon: <Icon name="logout" />,
      onClick: () => setOpenConfirmationDialog(true),
      title: "Log out"
    });

    // Every action closes the menu once it has run.
    return list.map((action) => ({
      ...action,
      onClick: (event) => {
        action.onClick(event);
        setMenuOpen(false);
      }
    }));
  }, [
    canInstall,
    isSocialProAdmin,
    navigate,
    openNotificationDrawer,
    promptInstall,
    setMenuOpen,
    supportActions,
    unreadCount
  ]);

  const logoUrl =
    userProfile?.profileLogo || userOrganisation?.organisationLogo || "";
  const firstName = userProfile?.firstName || "";
  const organisationName = userOrganisation?.displayName || "";

  const renderAvatar = (size: number) => (
    <ProfileAvatarWrap size={size}>
      <ProfileAvatar
        size={size}
        src={logoUrl || undefined}
        alt=""
        data-test-id="representative-avatar"
      >
        {!logoUrl && <Logo variant="mark" width={LOGO_FALLBACK_SIZE} />}
      </ProfileAvatar>
      <ProfileStatusDot />
    </ProfileAvatarWrap>
  );

  const renderMenu = () =>
    isDrawOpen ? (
      <ProfileMenu role="menu">
        {actions.map((action) => (
          <ProfileMenuItem
            key={action.dataTestId ?? String(action.title)}
            role="menuitem"
            onClick={action.onClick}
            highlighted={isHighlighted(action)}
            data-test-id={action.dataTestId}
          >
            <ProfileMenuIcon highlighted={isHighlighted(action)}>
              {applyIconProps(action.icon, { size: MENU_ICON_SIZE })}
            </ProfileMenuIcon>
            <ProfileMenuLabel>{action.title}</ProfileMenuLabel>
            {action.showBadge ? <ProfileMenuBadge /> : null}
          </ProfileMenuItem>
        ))}
      </ProfileMenu>
    ) : (
      <ProfileRailMenu role="menu">
        {actions.map((action) => (
          <BaseTooltip
            key={action.dataTestId ?? String(action.title)}
            title={action.title}
            placement="right"
          >
            <ProfileRailMenuButton
              role="menuitem"
              onClick={action.onClick}
              highlighted={isHighlighted(action)}
              data-test-id={action.dataTestId}
              aria-label={
                typeof action.title === "string" ? action.title : undefined
              }
            >
              {applyIconProps(action.icon, { size: MENU_ICON_SIZE + 1 })}
              {action.showBadge ? <ProfileRailMenuBadge /> : null}
            </ProfileRailMenuButton>
          </BaseTooltip>
        ))}
      </ProfileRailMenu>
    );

  const renderLauncher = () => {
    if (isLoading) {
      return isDrawOpen ? (
        <ProfileCard expanded disabled>
          <Skeleton
            variant="circular"
            width={CARD_AVATAR}
            height={CARD_AVATAR}
          />
          <Skeleton variant="text" width={120} height={16} />
        </ProfileCard>
      ) : (
        <Skeleton variant="circular" width={CARD_AVATAR} height={CARD_AVATAR} />
      );
    }

    // One control in both states — it folds down to the avatar rather than
    // being replaced by a different button halfway through the animation.
    return (
      <BaseTooltip
        title={
          isDrawOpen || isMenuOpen ? "" : `${firstName} · ${organisationName}`
        }
        placement="right"
      >
        <ProfileCard
          expanded={isDrawOpen}
          onClick={() => setMenuOpen(!isMenuOpen)}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-label={
            isDrawOpen ? undefined : `${firstName} · ${organisationName}`
          }
          data-test-id="profile-launcher"
        >
          {renderAvatar(CARD_AVATAR)}
          <FoldingText visible={isDrawOpen}>
            <ProfileName data-test-id="profile-name">{firstName}</ProfileName>
            <ProfileOrg
              data-test-id="organisation-name"
              title={organisationName}
            >
              {organisationName}
            </ProfileOrg>
          </FoldingText>
          <FoldingChevron visible={isDrawOpen}>
            <Icon name={isMenuOpen ? "chevron-down" : "chevron-up"} size="xs" />
          </FoldingChevron>
        </ProfileCard>
      </BaseTooltip>
    );
  };

  return (
    <>
      <ProfileFooter>
        {/* The rule reads as a divider between menu and card once the menu is
            open, so it goes while the menu is up. The card is bottom-anchored
            and does not move. */}
        {isMenuOpen ? null : <ProfileFooterRule open={isDrawOpen} />}
        <ClickAwayListener
          onClickAway={() => {
            if (isMenuOpen) setMenuOpen(false);
          }}
        >
          <ProfileAnchor>
            {isMenuOpen ? renderMenu() : null}
            {renderLauncher()}
          </ProfileAnchor>
        </ClickAwayListener>
      </ProfileFooter>
      <ConfirmationDialog
        open={openConfirmationDialog}
        onCancel={() => setOpenConfirmationDialog(false)}
        onConfirm={handleLogout}
        title="Logout"
        message="Are you sure you want to logout of your account?"
        intent="primary"
        icon={<Icon name="logout" tone="white" />}
      />
    </>
  );
};

export default MainSidebarProfileSection;
